import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { fetchCurrentSession } from '../utils/sessionFetch';

interface ImpersonationState {
  active: boolean;
  tenantId?: string;
  tenantName?: string;
  reasonPreset?: string;
  reasonText?: string;
  startedAt?: string;
  end: () => Promise<void>;
}

const ImpersonationContext = createContext<ImpersonationState>({
  active: false,
  end: async () => undefined,
});

export const ImpersonationProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<Omit<ImpersonationState, 'end'>>({ active: false });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchCurrentSession();
        if (!r.ok) return;
        const body = await r.json();
        if (cancelled) return;
        if (body?.impersonation?.active) {
          setData({
            active: true,
            tenantId: body.impersonation.tenant_id,
            tenantName: body.impersonation.tenant_name,
            reasonPreset: body.impersonation.reason_preset,
            reasonText: body.impersonation.reason_text,
            startedAt: body.impersonation.started_at,
          });
        }
      } catch { /* swallow — context defaults to inactive */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const end = async () => {
    try {
      await fetch('/api/impersonate', { method: 'DELETE', credentials: 'include' });
    } finally {
      // After ending, jump back to /manage. Cookie is cleared server-side.
      window.location.href = '/manage/tenants';
    }
  };

  const value = useMemo<ImpersonationState>(() => ({ ...data, end }), [data]);
  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
};

export const useImpersonation = () => useContext(ImpersonationContext);
